import { useState, useEffect, useRef } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import API_BASE from '../config';

const SIGNAL_LABELS = {
  funding: 'Funding Round',
  hiring: 'Hiring Activity',
  leadership_change: 'Leadership Change',
  news: 'Company News',
  tech_change: 'Technology Change',
};

const SIGNAL_COLOURS = {
  funding: '#276749',
  hiring: '#2B6CB0',
  leadership_change: '#8B1A1A',
  news: '#B7791F',
  tech_change: '#553C9A',
};

function fmtTime(ts) {
  const diff = Date.now() - new Date(ts);
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function BellIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export function Layout({ children }) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, isTeamMember, isClientAdmin, isClientOrMember, clientId } = useRole();

  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSignals, setDropdownSignals] = useState([]);
  const signalsRef = useRef([]);       // full signal objects from API
  const dropdownRef = useRef(null);    // for click-outside detection

  // ── Credit balance ─────────────────────────────────────────────────
  const [credits, setCredits] = useState(null);
  const [creditDropdownOpen, setCreditDropdownOpen] = useState(false);
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('ce_credit_banner_dismissed') === '1'; }
    catch { return false; }
  });
  const creditDropdownRef = useRef(null);

  // URL wins — supports multi-workspace customers who can navigate between workspaces they own.
  // Falls back to useRole's clientId (their default/home workspace) when no client_id in URL.
  const urlClientIdMatch = location.pathname.match(/\/clients\/(\d+)/);
  const effectiveClientId = (urlClientIdMatch ? urlClientIdMatch[1] : null) || clientId;

  useEffect(() => {
    const handler = () => setTopupModalOpen(true);
    window.addEventListener('open-topup-modal', handler);
    return () => window.removeEventListener('open-topup-modal', handler);
  }, []);

  useEffect(() => {
    if (!effectiveClientId) { setCredits(null); return; }
    let cancelled = false;
    function refresh() {
      fetch(`${API_BASE}/clients/${effectiveClientId}/credits`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (!cancelled && data) setCredits(data); })
        .catch(() => {});
    }
    refresh();
    function onCreditChange() { refresh(); }
    window.addEventListener('ce:creditsChanged', onCreditChange);
    return () => { cancelled = true; window.removeEventListener('ce:creditsChanged', onCreditChange); };
  }, [effectiveClientId]);

  // Close credit dropdown on click outside
  useEffect(() => {
    if (!creditDropdownOpen) return;
    function handler(e) {
      if (creditDropdownRef.current && !creditDropdownRef.current.contains(e.target)) {
        setCreditDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [creditDropdownOpen]);

  // Banner threshold logic
  const showBanner = credits && !bannerDismissed && credits.total < 50;

  function dismissBanner() {
    setBannerDismissed(true);
    try { sessionStorage.setItem('ce_credit_banner_dismissed', '1'); } catch {}
  }

  // Pill colour based on percentage of monthly allocation
  function pillColour() {
    if (!credits) return { border: '#D4CFC9', text: '#4A5568', bg: '#fff' };
    const pct = credits.percent_remaining;
    if (pct === null || pct >= 20) return { border: '#8B1A1A', text: '#8B1A1A', bg: '#FFF5F5' };
    if (pct >= 10) return { border: '#B7791F', text: '#B7791F', bg: '#FFFAF0' };
    return { border: '#C53030', text: '#C53030', bg: '#FFF5F5' };
  }

    const path = location.pathname;

  // Fetch signals and compute badge count
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;
    const canSeeSignals = isSuperAdmin || isTeamMember || isClientAdmin;
    if (!canSeeSignals) return;

    const url = isClientOrMember && clientId
      ? `${API_BASE}/signals/today-ids?clientId=${clientId}`
      : `${API_BASE}/signals/today-ids`;

    function computeUnread() {
      if (!signalsRef.current.length) return 0;
      try {
        const readSet = new Set(JSON.parse(localStorage.getItem(`ce_read_signals_${userId}`) || '[]'));
        return signalsRef.current.filter(s => !readSet.has(s.id)).length;
      } catch { return 0; }
    }

    function refresh() {
      fetch(url)
        .then(r => r.json())
        .then(data => {
          signalsRef.current = data.signals || [];
          setUnreadCount(computeUnread());
        })
        .catch(() => {});
    }

    refresh();
    const timer = setInterval(refresh, 60000);

    function onSignalRead() { setUnreadCount(computeUnread()); }
    window.addEventListener('ce:signalRead', onSignalRead);
    window.addEventListener('storage', onSignalRead);

    return () => {
      clearInterval(timer);
      window.removeEventListener('ce:signalRead', onSignalRead);
      window.removeEventListener('storage', onSignalRead);
    };
  }, [user?.id, isSuperAdmin, isTeamMember, isClientAdmin, isClientOrMember, clientId]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleMouseDown(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [dropdownOpen]);

  function handleBellClick() {
    if (dropdownOpen) {
      setDropdownOpen(false);
      return;
    }
    const userId = user?.id;
    // Capture unread signals before marking them read
    let unread = [];
    if (userId) {
      try {
        const readSet = new Set(JSON.parse(localStorage.getItem(`ce_read_signals_${userId}`) || '[]'));
        unread = signalsRef.current.filter(s => !readSet.has(s.id));
      } catch {}
      // Mark all current signals as read
      const allIds = signalsRef.current.map(s => s.id);
      localStorage.setItem(`ce_read_signals_${userId}`, JSON.stringify(allIds));
      window.dispatchEvent(new Event('ce:signalRead'));
    }
    setDropdownSignals(unread.slice(0, 10));
    setUnreadCount(0);
    setDropdownOpen(true);
  }

  const link = (href, label, matchPath) => {
    const active = matchPath ? path.includes(matchPath) : path === href;
    return (
      <a href={href} style={{ fontSize: '0.83rem', color: active ? '#8B1A1A' : '#888', textDecoration: 'none', borderBottom: active ? '2px solid #8B1A1A' : '2px solid transparent', paddingBottom: '2px' }}>
        {label}
      </a>
    );
  };

  const dashboardPath = isClientOrMember ? '/workspace' : '/';

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #E8E4E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 2rem', height: '52px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: 1 }}>
          <img src="/logo.png" alt="Client Engine" style={{ height: '50px', width: 'auto', cursor: 'pointer' }} onClick={() => navigate(dashboardPath)} />
          {!isClientOrMember && (
            <>
              {link('/', 'Dashboard')}
              {link('/clients', 'Clients')}
              {link('/prompts', 'Prompts')}
              {link('/settings', 'Settings')}
              {(isSuperAdmin || isTeamMember) && link('/mail', 'Mail', '/mail')}
              {(isSuperAdmin || isTeamMember) && link('/documents', 'Documents')}
              {isSuperAdmin && link('/admin/accounts', 'Accounts', '/admin/accounts')}
              {isSuperAdmin && link('/admin/users', 'Users', '/admin/users')}
              {isSuperAdmin && link('/admin/costs', 'Costs', '/admin/costs')}
              {isSuperAdmin && link('/admin/social', 'Social', '/admin/social')}
              {isSuperAdmin && link('/admin/webhooks', 'Webhooks', '/admin/webhooks')}
            </>
          )}
          {isClientOrMember && clientId && (
            <>
              {link('/workspace', 'Dashboard')}
              {link(`/clients/${clientId}/discovery`, 'Discovery', '/discovery')}
              {link(`/clients/${clientId}/icp`, 'ICP', '/icp')}
              {link(`/clients/${clientId}/prospects`, 'Prospects', '/prospects')}
              {link(`/clients/${clientId}/sequences`, 'Sequences', '/sequences')}
              {link(`/clients/${clientId}/intelligence`, 'Intelligence', '/intelligence')}
              {link(`/clients/${clientId}/mail/inbox`, 'Mail', '/mail')}
              {isClientAdmin && link('/my-documents', 'Documents')}
              {isClientAdmin && link(`/clients/${effectiveClientId}/account`, 'Account', '/account')}
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Bell + dropdown */}
          {(isSuperAdmin || isTeamMember || isClientAdmin) && (
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={handleBellClick}
                style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: unreadCount > 0 ? '#8B1A1A' : '#888', padding: '0.25rem', display: 'flex', alignItems: 'center' }}
                title={unreadCount > 0 ? `${unreadCount} unread signal${unreadCount !== 1 ? 's' : ''}` : 'Signals'}
              >
                <BellIcon />
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-4px', backgroundColor: '#C53030', color: '#fff', fontSize: '0.58rem', fontWeight: '700', minWidth: '14px', height: '14px', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {dropdownOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '320px', backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden', zIndex: 200 }}>
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '0.5px solid #F0EDE8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#1A1A1A' }}>Signals</span>
                    <button onClick={() => { setDropdownOpen(false); navigate(dashboardPath); }} style={{ fontSize: '0.72rem', color: '#8B1A1A', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>View all →</button>
                  </div>

                  {dropdownSignals.length === 0 ? (
                    <p style={{ margin: 0, padding: '1.25rem 1rem', fontSize: '0.82rem', color: '#888', textAlign: 'center' }}>No unread signals.</p>
                  ) : (
                    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                      {dropdownSignals.map((s, i) => {
                        const colour = SIGNAL_COLOURS[s.signal_type] || '#888';
                        return (
                          <div
                            key={s.id}
                            onClick={() => { setDropdownOpen(false); navigate(dashboardPath); }}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', padding: '0.75rem 1rem', borderBottom: i < dropdownSignals.length - 1 ? '0.5px solid #F5F2EF' : 'none', cursor: 'pointer', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFAF8'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: colour, flexShrink: 0, marginTop: '0.35rem' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
                                <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '500', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company_name}</p>
                                <span style={{ fontSize: '0.68rem', color: '#AAA', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtTime(s.created_at)}</span>
                              </div>
                              <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: colour, fontWeight: '500' }}>{SIGNAL_LABELS[s.signal_type] || s.signal_type}</p>
                              {s.client_name && isSuperAdmin && (
                                <p style={{ margin: '0.1rem 0 0', fontSize: '0.68rem', color: '#AAA' }}>{s.client_name}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Workspace name pill */}
          {effectiveClientId && credits?.workspace_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, background: '#FAFAF8', color: '#4A5568', border: '1px solid #E8E4E0', borderRadius: '14px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={credits.workspace_name}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0 }}>
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{credits.workspace_name}</span>
            </div>
          )}

          {/* Credit pill */}
          {effectiveClientId && credits && (
            <div ref={creditDropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setCreditDropdownOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.3rem 0.7rem',
                  fontSize: '0.78rem', fontWeight: '600',
                  background: pillColour().bg,
                  color: pillColour().text,
                  border: '1px solid ' + pillColour().border,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                title={credits.account_pot != null ? `Pot: ${credits.account_pot} • This workspace: ${credits.total}` : `Monthly: ${credits.monthly_remaining}/${credits.monthly_allocation || 0} • Top-up: ${credits.topup_remaining}`}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8 12h8M12 8v8" />
                </svg>
                <span>
                  {credits.account_pot != null
                    ? <>Pot {(credits.account_pot || 0).toLocaleString('en-GB')} / {(credits.total || 0).toLocaleString('en-GB')} here</>
                    : <>{credits.total} credits</>}
                </span>
              </button>

              {creditDropdownOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '260px', backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden', zIndex: 200 }}>
                  <div style={{ padding: '0.85rem 1rem', borderBottom: '0.5px solid #F0EDE8' }}>
                    <p style={{ margin: 0, fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>Credits</p>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '1.4rem', fontWeight: '700', color: '#1A1A1A' }}>{credits.total}</p>
                    {credits.percent_remaining !== null && (
                      <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: pillColour().text, fontWeight: '500' }}>
                        {credits.percent_remaining}% of monthly allocation remaining
                      </p>
                    )}
                  </div>
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '0.5px solid #F0EDE8' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#4A5568' }}>
                      <span>Monthly</span>
                      <span style={{ fontWeight: '600' }}>{credits.monthly_remaining} / {credits.monthly_allocation || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#4A5568', marginTop: '0.3rem' }}>
                      <span>Top-up</span>
                      <span style={{ fontWeight: '600' }}>{credits.topup_remaining}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => { setCreditDropdownOpen(false); setTopupModalOpen(true); }}
                    style={{ display: 'block', width: '100%', padding: '0.7rem 1rem', fontSize: '0.82rem', fontWeight: '600', background: '#8B1A1A', color: '#fff', border: 'none', cursor: 'pointer' }}
                  >
                    Top up credits
                  </button>
                </div>
              )}
            </div>
          )}

                    <span style={{ fontSize: '0.82rem', color: '#888' }}>{user?.primaryEmailAddress?.emailAddress}</span>
          <button onClick={() => signOut()} style={{ fontSize: '0.82rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '5px', padding: '0.3rem 0.75rem', cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>
      {/* Low-credit banner */}
      {showBanner && (
        <div style={{ background: '#C53030', color: '#fff', padding: '0.65rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '500' }}>
              Low credits — you have <strong>{credits?.total || 0}</strong> remaining. Top up to keep things running.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setTopupModalOpen(true)} style={{ fontSize: '0.78rem', fontWeight: '600', background: '#fff', color: '#C53030', border: 'none', borderRadius: '5px', padding: '0.35rem 0.85rem', cursor: 'pointer' }}>Top up</button>
            <button onClick={dismissBanner} style={{ fontSize: '0.78rem', background: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '5px', padding: '0.35rem 0.7rem', cursor: 'pointer' }}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Top-up modal — real Stripe checkout */}
      {topupModalOpen && (
        <TopUpModal
          clientId={effectiveClientId}
          onClose={() => setTopupModalOpen(false)}
        />
      )}

            {children}
    </div>
  );
}

function TopUpModal({ clientId, onClose }) {
  const [amount, setAmount] = useState('25');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const presets = [10, 25, 50, 100, 500, 2000];
  const numAmount = parseFloat(amount) || 0;
  // Preview credits using same formula as backend
  const baseCredits = Math.round(numAmount / 0.05);
  let bonusPct = 0;
  if (numAmount >= 2000) bonusPct = 20;
  else if (numAmount >= 500) bonusPct = 10;
  const totalCredits = Math.round(baseCredits * (1 + bonusPct / 100));

  async function handleCheckout() {
    setError(null);
    if (numAmount < 5) { setError('Minimum top-up is £5'); return; }
    if (!clientId) { setError('No workspace selected'); return; }
    setLoading(true);
    try {
      const res = await fetch(API_BASE + '/clients/' + clientId + '/checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gbp_amount: numAmount }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Checkout failed');
      if (data.url) window.location.href = data.url;
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '480px', width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem', fontWeight: '700', color: '#1A1A1A' }}>Top up credits</h2>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>£0.05 per credit · £5 minimum</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
          {presets.map(p => (
            <button key={p} onClick={() => setAmount(String(p))}
              style={{
                padding: '0.65rem 0.5rem', fontSize: '0.88rem', fontWeight: '600',
                background: numAmount === p ? '#8B1A1A' : '#fff',
                color: numAmount === p ? '#fff' : '#1A1A1A',
                border: '1px solid ' + (numAmount === p ? '#8B1A1A' : '#D4CFC9'),
                borderRadius: '6px', cursor: 'pointer'
              }}>
              £{p}{p >= 2000 ? ' (+20%)' : p >= 500 ? ' (+10%)' : ''}
            </button>
          ))}
        </div>

        <label style={{ display: 'block', fontSize: '0.78rem', color: '#888', marginBottom: '0.35rem', fontWeight: '500' }}>Or enter custom amount (£)</label>
        <input type="number" min="5" step="1" value={amount} onChange={e => setAmount(e.target.value)}
          style={{ width: '100%', padding: '0.6rem 0.85rem', fontSize: '0.95rem', border: '1px solid #D4CFC9', borderRadius: '6px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', marginBottom: '1rem' }} />

        <div style={{ background: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#1A1A1A', marginBottom: bonusPct > 0 ? '0.35rem' : 0 }}>
            <span>{baseCredits.toLocaleString()} credits</span>
            <span style={{ color: '#888' }}>£{numAmount.toFixed(2)}</span>
          </div>
          {bonusPct > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#276749', fontWeight: '600' }}>
              <span>+ {(totalCredits - baseCredits).toLocaleString()} bonus credits ({bonusPct}%)</span>
            </div>
          )}
          <div style={{ borderTop: '0.5px solid #E8E4E0', marginTop: '0.5rem', paddingTop: '0.5rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: '700', color: '#1A1A1A' }}>
            <span>Total: {totalCredits.toLocaleString()} credits</span>
            <span>£{numAmount.toFixed(2)}</span>
          </div>
        </div>

        {error && <p style={{ margin: '0 0 0.85rem', fontSize: '0.82rem', color: '#C53030' }}>{error}</p>}

        <button onClick={handleCheckout} disabled={loading || numAmount < 5}
          style={{
            display: 'block', width: '100%', padding: '0.8rem', fontSize: '0.95rem', fontWeight: '600',
            background: numAmount < 5 ? '#D4CFC9' : '#8B1A1A',
            color: '#fff', border: 'none', borderRadius: '6px',
            cursor: loading || numAmount < 5 ? 'default' : 'pointer'
          }}>
          {loading ? 'Redirecting to Stripe...' : 'Continue to payment'}
        </button>
        <p style={{ margin: '0.85rem 0 0', fontSize: '0.72rem', color: '#888', textAlign: 'center' }}>
          Secure payment via Stripe. Credits are added immediately on successful payment.
        </p>
      </div>
    </div>
  );
}
